import { CheckCircle } from 'lucide-react';
import { usePrimaryColor } from '../../branding/usePrimaryColor';
import { formatCurrency } from '../../utils/currency';
import type { OutstandingPaymentsByState } from '../../types/dashboard';

const STATE_LABELS: Record<string, string> = {
  fail_stripe: 'Failed',
  fail_unknown: 'Failed',
  processing: 'Processing',
  to_create: 'Pending',
};

/** States that represent actual failures (amber/red). */
const FAILED_STATES = new Set(['fail_stripe', 'fail_unknown']);

interface OutstandingPaymentsProps {
  totalAmount: number;
  count: number;
  byState: OutstandingPaymentsByState[];
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  onRetry?: () => void;
}

export function OutstandingPayments({
  totalAmount,
  count,
  byState,
  loading = false,
  error = false,
  forbidden = false,
  onRetry,
}: OutstandingPaymentsProps) {
  const primaryColor = usePrimaryColor();

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Outstanding payments</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Outstanding payments</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load payment status.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && count === 0 && byState.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-36 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-8 w-28 bg-canvas animate-pulse rounded" />
          <div className="h-3 w-24 bg-canvas animate-pulse rounded" />
          <div className="space-y-2 pt-2">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="h-3 w-24 bg-canvas animate-pulse rounded" />
                <div className="h-3 w-14 bg-canvas animate-pulse rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state: all payments are up to date
  if (count === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Outstanding payments</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <CheckCircle size={20} className="text-emerald-500" />
          <p className="text-[11.5px] text-ink-4">All payments are up to date.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <h3 className="text-[14px] font-semibold">Outstanding payments</h3>
      </div>

      {/* Main figure + breakdown */}
      <div className="px-4 pt-4 pb-3">
        {/* Total */}
        <div className="mb-1">
          <span className="text-[28px] font-semibold leading-none num">
            ${formatCurrency(totalAmount)}
          </span>
        </div>
        <p className="text-[11.5px] text-ink-4 mb-4">
          {count} payment{count !== 1 ? 's' : ''} outstanding
        </p>

        {/* By-state breakdown */}
        <div className="space-y-2">
          {byState.map((row) => {
            const isFailed = FAILED_STATES.has(row.state);
            return (
              <div key={row.state} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: isFailed ? '#d97706' : primaryColor }}
                  />
                  <span className="text-[12px] text-ink-3">
                    {STATE_LABELS[row.state] ?? row.state}
                  </span>
                  <span className="text-[11px] text-ink-4 num">
                    ({row.count})
                  </span>
                </div>
                <span className={`text-[12px] font-medium num ${isFailed ? 'text-amber-600' : 'text-ink-2'}`}>
                  ${formatCurrency(row.amount)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
